import {
  ApplicationRef,
  CUSTOM_ELEMENTS_SCHEMA,
  DoBootstrap,
  isDevMode,
  NgModule,
  provideAppInitializer,
  inject
} from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { Location, LocationStrategy, PathLocationStrategy } from '@angular/common';
import { BrowserModule } from '@angular/platform-browser';
import {
  HTTP_INTERCEPTORS,
  HttpClient,
  provideHttpClient,
  withInterceptorsFromDi,
  withXhr
} from '@angular/common/http';
import { tap } from 'rxjs/operators';
import { ConfigService, IConfig } from './configuration/config.service';
import '@neogov/pattern-library/utility';
import { SharedModule } from './shared/shared.module';
import { DateInterceptor } from './shared/interceptors/date.interceptor';
import { NoCacheInterceptor } from './shared/interceptors/no-cache.interceptor';
import { NgSwBypassInterceptor } from './shared/interceptors/ngsw-bypass';
import { MaintenanceModeInterceptor } from './shared/interceptors/maintenance-mode.interceptor';
import { TokenInterceptor } from './shared/interceptors/token.interceptor';
import { RootRoutingModule } from './modules/root-routing.module';
import { AppComponent } from './app.component';
import { SidebarBuilderFactory } from './modules/common-features/sidebar/builders/sidebar-builder-factory';
import { FtSidebarBuilder } from './modules/common-features/sidebar/builders/ft-sidebar-builder';
import { PeSidebarBuilder } from './modules/common-features/sidebar/builders/pe-sidebar-builder';
import { PpSidebarBuilder } from './modules/common-features/sidebar/builders/pp-sidebar-builder';
import { MtSidebarBuilder } from './modules/common-features/sidebar/builders/mt-sidebar-builder';
import { AuthGuard } from '@app/shared/services/route-guards/auth.guard';
import { UnsavedChangesGuard } from '@app/shared/services/route-guards/unsaved-changes.guard';
import { CoreModule } from './shared/core.module';
import { ResponsiveGuard } from '@app/shared/services/route-guards/responsive.guard';
import { ConnectionStateInterceptor } from './shared/interceptors/connection-state.interceptor';
import { RoleGuard } from 'src/app/shared/services/route-guards/role.guard';
import { TargetSiteProgramGuard } from 'src/app/shared/services/route-guards/target-site-program.guard';
import { SidebarComponent } from 'src/app/modules/common-features/sidebar/sidebar.component';
import { SidebarItemComponent } from 'src/app/modules/common-features/sidebar/sidebar-item/sidebar-item.component';
import { SidebarMenuItemComponent } from 'src/app/modules/common-features/sidebar/sidebar-menu-item/sidebar-menu-item.component';
import { SidebarMenuContainerComponent } from 'src/app/modules/common-features/sidebar/sidebar-menu-container/sidebar-menu-container.component';
import { SidebarProgramSelectorComponent } from 'src/app/modules/common-features/sidebar/sidebar-program-selector/sidebar-program-selector.component';
import { NavbarComponent } from 'src/app/modules/common-features/navbar/navbar.component';
import { TopNavBarComponent } from 'src/app/modules/common-features/top-nav-bar/top-nav-bar.component';
import { NotificationComponent } from 'src/app/modules/common-features/notification/notification.component';
import { NotificationItemComponent } from 'src/app/modules/common-features/notification/notification-item/notification-item.component';
import { RedirectGuard } from '@app/shared/services/route-guards/redirect.guard';
import { RedirectEidGuard } from '@app/shared/services/route-guards/redirect-eid.guard';
import { EnableOnSiteGuard } from '@app/shared/services/route-guards/enable-on-site.guard';
import { ActiveDirectoryConfigService, AutoUpdateService } from '@app/shared/services';
import { ServiceWorkerModule } from '@angular/service-worker';
import { isInIframe } from './shared/utils';

const CONFIG_URL = './assets/environment.json';

function initializeAppFactory(httpClient: HttpClient, configService: ConfigService) {
  return () => {
    if (configService.getConfig()) {
      // Already pre-loaded by main.ts — skip duplicate HTTP request
      return Promise.resolve();
    }
    return httpClient
      .get(CONFIG_URL)
      .pipe(
        tap((customConfig) => {
          configService.setConfig({ ...(<IConfig>customConfig) });
        })
      )
      .toPromise();
  };
}

import { ApiModule } from '@app/api/api.module';
import { GainsightModule } from '@app/gainsight/gainsight.module';
import { HttpErrorHandlerInterceptor } from '@app/shared/interceptors/http-error-handler.interceptor';
import { AuthConfigModule } from './auth/auth-config.module';
import { SubnavComponent } from './modules/common-features/subnav/subnav.component';
import { TopSubNavComponent } from './modules/common-features/top-sub-nav/top-sub-nav.component';
import { PatternLibraryModule } from '@shared/pattern-library.module';
import { NeoNativeDateModule } from '@neogov/pattern-library/core';
import { ProgramSwitcherComponent } from './modules/common-features/subnav/program-switcher.component';

@NgModule({
  declarations: [
    AppComponent,
    SidebarComponent,
    SidebarItemComponent,
    SidebarMenuItemComponent,
    SidebarMenuContainerComponent,
    SidebarProgramSelectorComponent,
    NavbarComponent,
    TopNavBarComponent,
    NotificationComponent,
    NotificationItemComponent,
    SubnavComponent,
    TopSubNavComponent,
    ProgramSwitcherComponent
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [
    BrowserAnimationsModule,
    BrowserModule,
    SharedModule,
    GainsightModule,
    ApiModule.forRoot(),
    CoreModule.forRoot(),
    RootRoutingModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: !isDevMode(),
      registrationStrategy: 'registerWhenStable:30000',
      updateViaCache: 'none'
    }),
    AuthConfigModule,
    PatternLibraryModule,
    NeoNativeDateModule
  ],
  providers: [
    SidebarBuilderFactory,
    FtSidebarBuilder,
    PeSidebarBuilder,
    PpSidebarBuilder,
    MtSidebarBuilder,
    AuthGuard,
    UnsavedChangesGuard,
    ResponsiveGuard,
    RoleGuard,
    TargetSiteProgramGuard,
    RedirectGuard,
    RedirectEidGuard,
    EnableOnSiteGuard,
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ConnectionStateInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: HttpErrorHandlerInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: DateInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NoCacheInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: NgSwBypassInterceptor,
      multi: true
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MaintenanceModeInterceptor,
      multi: true
    },
    Location,
    {
      provide: LocationStrategy,
      useClass: PathLocationStrategy
    },
    provideHttpClient(withXhr(), withInterceptorsFromDi()),
    provideAppInitializer(() => {
      const httpClient = inject(HttpClient);
      const configService = inject(ConfigService);
      return initializeAppFactory(httpClient, configService)();
    })
  ]
})
export class AppModule implements DoBootstrap {
  constructor(private readonly adalSvc: ActiveDirectoryConfigService, autoUpdateService: AutoUpdateService) {
    autoUpdateService.init();
  }

  public ngDoBootstrap(applicationRef: ApplicationRef) {
    if (isInIframe()) {
      this.adalSvc.init().subscribe();
    } else {
      applicationRef.bootstrap(AppComponent);
    }
  }
}
